import { 
  Card, 
  CardHeader, 
  CardTitle, 
  CardContent,
  Badge 
} from '@etherisc/ui-kit';
import { AlertTriangleIcon, XCircleIcon } from 'lucide-react';

interface ValidationError {
  field: string;
  message: string;
  schema?: string;
  data?: unknown;
  value?: unknown;
}

interface ValidationErrorReportProps {
  errors: ValidationError[];
  title?: string;
  className?: string;
}

export function ValidationErrorReport({ 
  errors, 
  title = "Validation Errors", 
  className = "" 
}: ValidationErrorReportProps) {
  if (!errors || errors.length === 0) {
    return null;
  }

  const formatFieldName = (field: string): string => {
    return field
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .replace(/\./g, ' → ')
      .replace(/productConfig → /, '')
      .replace(/_/g, ' ')
      .trim();
  };

  const formatErrorMessage = (message: string): string => {
    const mappings: Record<string, string> = {
      'must have required property': 'is missing a required field',
      'must NOT have additional properties': 'contains an unrecognized field',
      'must be equal to one of the allowed values': 'has an invalid value',
      'must be >= ': 'must be at least ',
      'must be <= ': 'must be at most ',
      'must be string': 'must be a text value',
      'must be number': 'must be a numeric value',
      'must be integer': 'must be a whole number',
      'must be boolean': 'must be true or false',
      'must be array': 'must be a list of values',
      'must be object': 'must be a structured value',
      'must match pattern': 'has an invalid format',
      'must NOT be valid': 'has a conflicting value',
    };

    let result = message;
    for (const [pattern, replacement] of Object.entries(mappings)) {
      if (result.includes(pattern)) {
        result = result.replace(pattern, replacement);
      }
    }
    return result;
  };

  const getErrorSeverity = (error: ValidationError): 'error' | 'warning' => {
    // Treat schema validation errors as more severe
    if (error.schema) return 'error';
    return 'warning';
  };

  const groupedErrors = errors.reduce((acc, error) => {
    const severity = getErrorSeverity(error);
    if (!acc[severity]) acc[severity] = [];
    acc[severity].push(error);
    return acc;
  }, {} as Record<string, ValidationError[]>);

  return (
    <Card className={`border-red-200 bg-red-50 ${className}`}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-red-800">
          <AlertTriangleIcon size={20} />
          {title}
          <Badge variant="destructive" className="ml-2">
            {errors.length} issue{errors.length > 1 ? 's' : ''}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {groupedErrors.error && (
          <div>
            <h4 className="font-medium text-red-900 mb-2 flex items-center gap-2">
              <XCircleIcon size={16} />
              Critical Errors ({groupedErrors.error.length})
            </h4>
            <div className="space-y-2">
              {groupedErrors.error.map((error, index) => (
                <div key={index} className="bg-white border border-red-200 rounded-md p-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-medium text-red-900">{formatFieldName(error.field)}</p>
                      <p className="text-sm text-red-700 mt-1">{formatErrorMessage(error.message)}</p>
                    </div>
                  </div>
                  {(error.schema || error.data !== undefined) && (
                    <details className="mt-2">
                      <summary className="text-xs text-red-600 cursor-pointer hover:text-red-800">
                        Show technical details
                      </summary>
                      <div className="text-xs bg-red-100 p-2 rounded mt-1 overflow-auto space-y-1">
                        {error.schema && (
                          <p className="font-mono text-red-600">Schema: {error.schema}</p>
                        )}
                        {error.data !== undefined && (
                          <pre className="font-mono text-red-600">
                            {typeof error.data === 'string' 
                              ? error.data 
                              : JSON.stringify(error.data, null, 2)
                            }
                          </pre>
                        )}
                      </div>
                    </details>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {groupedErrors.warning && (
          <div>
            <h4 className="font-medium text-orange-900 mb-2 flex items-center gap-2">
              <AlertTriangleIcon size={16} />
              Warnings ({groupedErrors.warning.length})
            </h4>
            <div className="space-y-2">
              {groupedErrors.warning.map((error, index) => (
                <div key={index} className="bg-white border border-orange-200 rounded-md p-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-medium text-orange-900">{formatFieldName(error.field)}</p>
                      <p className="text-sm text-orange-700 mt-1">{formatErrorMessage(error.message)}</p>
                    </div>
                  </div>
                  {error.value !== undefined && (
                    <p className="text-xs text-orange-600 mt-1">
                      Current value: <span className="font-mono">{String(error.value)}</span>
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="text-xs text-red-600 border-t border-red-200 pt-3">
          💡 Fix these issues before saving to ensure your product configuration is valid.
        </div>
      </CardContent>
    </Card>
  );
}
